<script setup lang="ts">
import { buildAboutLd } from "~/utils/seo/aboutLd";
import { buildLodgingBusinessLd } from "~/utils/lodgingBusinessSchema";

const { locale, t } = useI18n();
const route = useRoute();
const config = useRuntimeConfig();
const localePath = useLocalePath();

const { data: page } = await useAsyncData(
  `page-about-${locale.value}`,
  async () => {
    const collection = ("pages_" + locale.value) as "pages_en";
    return await queryCollection(collection)
      .where("slug", "=", "about")
      .first();
  },
  { watch: [locale] }
);

useLocalisedSeo(page);

// JSON-LD graph: full LodgingBusiness (geo, amenityFeature, priceRange,
// checkinTime/checkoutTime, aggregateRating) + AboutPage + BreadcrumbList
// + per-corner TouristAttraction. Previously /about only referenced
// #lodgingbusiness by @id without declaring it, making the reference
// dangle when Google crawled this page standalone. Now the full entity
// is declared here too, matching the homepage — same @id, so Google
// de-dupes across pages.
const { data: lodgingStats } = await useAsyncData(
  "lodging-stats",
  // `as string` sidesteps a typed-router route-matching type explosion
  // (TS2321 excessive stack depth); the explicit generic keeps the result typed.
  () =>
    $fetch<{ priceRange: string; numberOfRooms: number }>(
      "/api/apartments/lodging-stats" as string
    ),
  { default: () => ({ priceRange: "$$", numberOfRooms: 13 }) }
);

const lodgingLd = buildLodgingBusinessLd({
  email: (config.public.contactEmail as string) || "info@georgiadream.ge",
  whatsappNumber: (config.public.whatsappNumber as string) || "+995505556117",
  sameAs: [
    config.public.faceBookUrl as string | undefined,
    config.public.instagramUrl as string | undefined,
    config.public.telegramLink as string | undefined,
  ].filter((u): u is string => typeof u === "string" && u.length > 0),
  locale: locale.value as "en" | "ru" | "ka",
  priceRange: lodgingStats.value?.priceRange,
  numberOfRooms: lodgingStats.value?.numberOfRooms,
});

const jsonLd = computed(() => {
  if (!page.value) return null;
  const homePath = locale.value === "en" ? "/" : `/${locale.value}`;
  const aboutLd = buildAboutLd(
    {
      title: page.value.title,
      meta_description: page.value.meta_description,
      description: page.value.description,
      hero_image: page.value.hero_image,
      og_image: page.value.og_image,
      last_updated: page.value.last_updated as string | undefined,
      corners_gallery: page.value.corners_gallery,
    },
    {
      pagePath: route.path,
      homePath,
      homeLabel: t("nav.home"),
    }
  );
  return {
    "@context": "https://schema.org",
    "@graph": [
      ...(lodgingLd["@graph"] as Array<Record<string, unknown>>),
      ...(aboutLd["@graph"] as Array<Record<string, unknown>>),
    ],
  };
});

useHead({
  script: () =>
    jsonLd.value
      ? [
          {
            type: "application/ld+json",
            innerHTML: JSON.stringify(jsonLd.value),
          },
        ]
      : [],
});

// On phones the default reveal threshold (~15% in view, -80px bottom margin)
// often misses fast finger-scroll — sections flick past while still at opacity:0.
// Trigger ~280px before the section enters the viewport on small screens;
// keep the default behavior on tablet/desktop where it already works well.
useReveal(
  import.meta.client && window.matchMedia('(max-width: 768px)').matches
    ? { threshold: 0, rootMargin: '0px 0px 280px 0px' }
    : undefined,
);
</script>

<template>
  <div v-if="page">
    <!-- Hero — static photo, H1 + intro only (no CTA / TrustEyebrow / video) -->
    <section
      class="relative flex min-h-[40vh] w-full items-center overflow-hidden py-10 sm:py-14 md:min-h-[52vh] md:py-20"
      aria-labelledby="about-hero-title"
    >
      <NuxtImg
        v-if="page.hero_image"
        :src="page.hero_image"
        :alt="page.hero_image_alt ?? page.title"
        loading="eager"
        fetchpriority="high"
        format="webp"
        sizes="xs:100vw sm:100vw md:100vw lg:1280px xl:1536px"
        class="absolute inset-0 h-full w-full object-cover"
      />
      <div
        aria-hidden="true"
        class="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/70 to-slate-900/90"
      />
      <div class="container relative z-10 text-center">
        <h1
          id="about-hero-title"
          class="mt-6 font-display text-3xl italic leading-tight text-balance [overflow-wrap:anywhere] text-white drop-shadow sm:text-4xl md:mt-10 md:text-5xl lg:text-6xl"
        >
          {{ page.title }}
        </h1>
        <p
          v-if="page.intro"
          data-speakable="true"
          class="mx-auto mt-4 max-w-3xl text-base text-white/90 sm:text-lg md:mt-6 md:text-xl"
        >
          {{ page.intro }}
        </p>
      </div>
    </section>

    <div
      aria-hidden="true"
      class="pointer-events-none h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
    />

    <div v-if="page.exceptional_block" class="cv-auto">
      <AboutExceptionalBlock :block="page.exceptional_block" />
    </div>

    <div
      aria-hidden="true"
      class="pointer-events-none h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
    />

    <div v-if="page.rhythm_block" class="cv-auto">
      <AboutRhythmBlock :block="page.rhythm_block" />
    </div>

    <div
      aria-hidden="true"
      class="pointer-events-none h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
    />

    <div v-if="page.privacy_block" class="cv-auto">
      <AboutPrivacyBlock :block="page.privacy_block" />
    </div>

    <div
      aria-hidden="true"
      class="pointer-events-none h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
    />

    <div v-if="page.parkland_block" class="cv-auto">
      <AboutParklandBlock :block="page.parkland_block" />
    </div>

    <div
      aria-hidden="true"
      class="pointer-events-none h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
    />

    <div v-if="page.corners_gallery" class="cv-auto">
      <AboutCornersGallery :block="page.corners_gallery" />
    </div>

    <div v-if="page.further" class="cv-auto">
      <AboutFurtherBlock :block="page.further" />
    </div>

    <section
      class="cv-auto relative overflow-hidden bg-gradient-to-b from-white via-amber-50/30 to-white py-12 md:py-16 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
    >
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-32 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
      />
      <div data-reveal class="relative container mx-auto max-w-3xl px-4 text-center">
        <h2
          class="font-display text-3xl italic leading-tight text-slate-900 md:text-4xl dark:text-white"
        >
          {{ $t('about.cta.final.heading') }}
        </h2>
        <p
          class="mt-4 font-body text-base text-slate-600 md:text-lg dark:text-slate-300"
        >
          {{ $t('about.cta.final.subtext') }}
        </p>
        <div class="mt-8">
          <NuxtLink
            :to="localePath('/') + '#apartments'"
            class="inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3 font-body text-sm font-semibold uppercase tracking-[0.18em] text-slate-900 shadow-sm transition-colors hover:bg-amber-300"
          >
            {{ $t('amenities.cta.view_apartments') }}
            <svg
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>
